import { useCallback, useState } from "react";
import { customFetch } from "@workspace/api-client-react";

// Generate a UUID-like tempId client-side (not using external uuid library)
function generateTempId(): string {
  return (
    "temp-" +
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
}

/**
 * Manages uploading multiple files concurrently, re-using the core upload
 * logic from useFileUpload (hash → presign → upload → finalize).
 */

export type UploadKind =
  | "rfp_source"
  | "required_doc"
  | "attachment"
  | "brand_guide"
  | "agency_doc"
  | "past_rfp"
  | "client_doc";

export type UploadItemStatus = "idle" | "uploading" | "done" | "error";

export interface UploadItem {
  tempId: string;
  filename: string;
  byteSize: number;
  status: UploadItemStatus;
  progressPct: number;
  fileId: string | null;
  error: string | null;
}

const ALLOWED_MIME = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
]);

const MAX_BYTES = 10 * 1024 * 1024;

interface UploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  expiresAt: string;
}

interface FinalizeResponse {
  id: string;
  kind: string;
  filename: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  status: "pending" | "ready";
  createdAt: string;
}

export interface UseFileUploadListOptions {
  kind: UploadKind;
  projectId?: string;
}

export function useFileUploadList(opts?: UseFileUploadListOptions) {
  const [items, setItems] = useState<UploadItem[]>([]);

  const addFiles = useCallback(
    async (files: File[]) => {
      const kind = opts?.kind ?? "rfp_source";

      for (const file of files) {
        const tempId = generateTempId();
        const newItem: UploadItem = {
          tempId,
          filename: file.name,
          byteSize: file.size,
          status: "idle",
          progressPct: 0,
          fileId: null,
          error: null,
        };

        setItems((prev) => [...prev, newItem]);

        // Start upload immediately.
        try {
          // 1. Validate.
          if (file.size > MAX_BYTES) {
            const err = "File is larger than the 10 MB limit.";
            setItems((prev) =>
              prev.map((item) =>
                item.tempId === tempId ? { ...item, status: "error", error: err } : item,
              ),
            );
            continue;
          }
          if (!ALLOWED_MIME.has(file.type)) {
            const err = `Unsupported file type: ${file.type || "unknown"}`;
            setItems((prev) =>
              prev.map((item) =>
                item.tempId === tempId ? { ...item, status: "error", error: err } : item,
              ),
            );
            continue;
          }

          // 2. Hash.
          setItems((prev) =>
            prev.map((item) =>
              item.tempId === tempId ? { ...item, status: "uploading", progressPct: 5 } : item,
            ),
          );
          const sha256 = await sha256Hex(file);

          // 3. Presign.
          setItems((prev) =>
            prev.map((item) => (item.tempId === tempId ? { ...item, progressPct: 15 } : item)),
          );
          const { fileId, uploadUrl } = await customFetch<UploadUrlResponse>(
            "/api/v1/files/upload-url",
            {
              method: "POST",
              body: JSON.stringify({
                filename: file.name,
                contentType: file.type,
                byteSize: file.size,
                kind,
                projectId: opts?.projectId ?? null,
              }),
            },
          );

          // 4. Upload to R2.
          setItems((prev) =>
            prev.map((item) => (item.tempId === tempId ? { ...item, progressPct: 25 } : item)),
          );
          await putToR2(uploadUrl, file, (pct) =>
            setItems((prev) =>
              prev.map((item) =>
                item.tempId === tempId
                  ? { ...item, progressPct: 25 + Math.round(pct * 0.65) }
                  : item,
              ),
            ),
          );

          // 5. Finalize.
          setItems((prev) =>
            prev.map((item) => (item.tempId === tempId ? { ...item, progressPct: 92 } : item)),
          );
          await customFetch<FinalizeResponse>(`/api/v1/files/${fileId}/finalize`, {
            method: "POST",
            body: JSON.stringify({ sha256 }),
          });

          // Done.
          setItems((prev) =>
            prev.map((item) =>
              item.tempId === tempId ? { ...item, status: "done", progressPct: 100, fileId } : item,
            ),
          );
        } catch (err) {
          const message = (err as Error).message ?? "Upload failed.";
          setItems((prev) =>
            prev.map((item) =>
              item.tempId === tempId ? { ...item, status: "error", error: message } : item,
            ),
          );
        }
      }
    },
    [opts?.kind, opts?.projectId],
  );

  const removeItem = useCallback((tempId: string) => {
    setItems((prev) => prev.filter((item) => item.tempId !== tempId));
  }, []);

  const reset = useCallback(() => {
    setItems([]);
  }, []);

  const allFinishedSuccessfully = items.length > 0 && items.every((item) => item.status === "done");

  return {
    items,
    addFiles,
    removeItem,
    reset,
    allFinishedSuccessfully,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function sha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function putToR2(url: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else
        reject(
          new Error(`Couldn't upload the file (${xhr.status}). Check your file storage settings.`),
        );
    };
    xhr.onerror = () =>
      reject(
        new Error("Couldn't reach the file storage service. Check the bucket's CORS settings."),
      );
    xhr.send(file);
  });
}
